import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Dropdown, Modal, Input, Empty, Space, Typography, message } from 'antd';
import { StarOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';

const { Text } = Typography;

// Saved views (docs §UX: saved views across list pages). A view is a named
// snapshot of the current URL query (search + filters + sort + page, as persisted
// by useUrlTableState). Stored per page in localStorage so they're personal and
// need no backend. Applying a view rewrites the URL query, so the page's existing
// state wiring reloads with those filters.

export interface SavedView {
  name: string;
  query: string; // URLSearchParams string, e.g. "q=foo&status=active"
}

const keyFor = (pageKey: string) => `kp-saved-views:${pageKey}`;

export function useSavedViews(pageKey: string) {
  const [params, setParams] = useSearchParams();
  const [views, setViews] = useState<SavedView[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(keyFor(pageKey)) || '[]');
    } catch {
      return [];
    }
  });

  const persist = useCallback(
    (next: SavedView[]) => {
      setViews(next);
      try {
        localStorage.setItem(keyFor(pageKey), JSON.stringify(next));
      } catch {
        /* ignore quota */
      }
    },
    [pageKey],
  );

  const save = useCallback(
    (name: string) => {
      const query = params.toString();
      const next = [...views.filter((v) => v.name !== name), { name, query }].sort((a, b) => a.name.localeCompare(b.name));
      persist(next);
    },
    [params, views, persist],
  );

  const apply = useCallback((v: SavedView) => setParams(new URLSearchParams(v.query), { replace: true }), [setParams]);
  const remove = useCallback((name: string) => persist(views.filter((v) => v.name !== name)), [views, persist]);

  return { views, save, apply, remove, currentQuery: params.toString() };
}

/** A "Views" dropdown + save-current control for a list page. */
export const SavedViews: React.FC<{ pageKey: string }> = ({ pageKey }) => {
  const { views, save, apply, remove, currentQuery } = useSavedViews(pageKey);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');

  const doSave = () => {
    const n = name.trim();
    if (!n) return;
    save(n);
    setSaveOpen(false);
    setName('');
    message.success(`View “${n}” saved`);
  };

  const items: MenuProps['items'] = useMemo(() => {
    if (views.length === 0) {
      return [{ key: 'empty', disabled: true, label: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No saved views" style={{ margin: 8 }} /> }];
    }
    return views.map((v) => ({
      key: v.name,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 200 }}>
          <span style={{ flex: 1 }} onClick={() => apply(v)}>{v.name}</span>
          <DeleteOutlined
            onClick={(e) => { e.stopPropagation(); remove(v.name); }}
            style={{ color: 'var(--color-danger)' }}
          />
        </div>
      ),
      onClick: () => apply(v),
    }));
  }, [views, apply, remove]);

  return (
    <Space>
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button icon={<StarOutlined />}>Views{views.length ? ` (${views.length})` : ''}</Button>
      </Dropdown>
      <Button icon={<PlusOutlined />} onClick={() => setSaveOpen(true)} disabled={!currentQuery}>
        Save view
      </Button>
      <Modal
        title="Save current view"
        open={saveOpen}
        onOk={doSave}
        onCancel={() => setSaveOpen(false)}
        okText="Save"
        okButtonProps={{ disabled: !name.trim() }}
      >
        <Text type="secondary">Saves the current search, filters and sort as a named view.</Text>
        <Input
          style={{ marginTop: 12 }}
          placeholder="e.g. Suspended TZ tenants"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onPressEnter={doSave}
          autoFocus
        />
      </Modal>
    </Space>
  );
};

export default SavedViews;
